/**
 * useNavigation Hook
 * 
 * Generic hook for building navigation menu from EspoCRM metadata.
 * Works with any tenant's API by accepting the API hooks as parameters.
 */

import { useMemo } from 'react';
import { EspoCrmMetadata, UserInfoWithAcl } from '../types/espoCrm';
import { MenuItems } from 'src/core/types/appConfig';
import { buildMenu } from '../utils/navigationBuilder';

/**
 * Configuration for useNavigation hook
 */
export interface UseNavigationConfig {
  /**
   * RTK Query hook for fetching metadata
   * Should return { data: EspoCrmMetadata, isLoading: boolean, error: any }
   */
  useMetadataQuery: () => {
    data?: EspoCrmMetadata;
    isLoading: boolean;
    error?: any;
  };
  
  /**
   * RTK Query hook for fetching user info with ACL
   * Should return { data: UserInfoWithAcl, isLoading: boolean, error: any }
   */
  useUserInfoQuery: () => {
    data?: UserInfoWithAcl;
    isLoading: boolean;
    error?: any;
  };
  
  /**
   * Optional RTK Query hook for fetching translations
   * Should return { data: Record<string, any>, isLoading: boolean, error: any }
   */
  useTranslationsQuery?: () => {
    data?: Record<string, any>;
    isLoading: boolean;
    error?: any;
  };
}

/**
 * Return type for useNavigation hook
 */
export interface UseNavigationResult {
  /**
   * Menu items built from EspoCRM metadata
   */
  menuItems: MenuItems[];
  
  /**
   * Whether metadata or user info is still loading
   */
  isLoading: boolean;
  
  /**
   * Error from metadata or user info query
   */
  error: any;
}

/**
 * Hook to build navigation menu from EspoCRM metadata
 * 
 * @param config - Configuration with API hooks
 * @returns Menu items, loading state, and error
 */
export function useNavigation(
  config: UseNavigationConfig
): UseNavigationResult {
  const { useMetadataQuery, useUserInfoQuery, useTranslationsQuery } = config;
  
  // Fetch metadata and user info
  const metadataQuery = useMetadataQuery();
  const userInfoQuery = useUserInfoQuery();
  
  // Optionally fetch translations
  const translationsQuery = useTranslationsQuery?.();
  
  // Build menu items from metadata and user info
  const menuItems = useMemo(() => {
    // If data is not available, return empty array
    if (!metadataQuery.data || !userInfoQuery.data) {
      return [];
    }
    
    // Get translations if available (don't block if loading)
    const translations = translationsQuery?.data || null;
    
    // Build menu using the navigation builder with translations
    return buildMenu(metadataQuery.data, userInfoQuery.data, translations);
  }, [metadataQuery.data, userInfoQuery.data, translationsQuery?.data]);
  
  // Determine loading state
  const isLoading = metadataQuery.isLoading || userInfoQuery.isLoading || (translationsQuery?.isLoading ?? false);
  
  // Determine error state
  const error = metadataQuery.error || userInfoQuery.error || translationsQuery?.error;
  
  return {
    menuItems,
    isLoading,
    error,
  };
}

